import { catchError, filter, map, mergeMap } from 'rxjs/operators'
import { Component, OnInit, Injectable, inject, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, Scroll } from '@angular/router'
import { Events } from 'depoto-core/src/utils'
import { CoreService, LogoutService } from './services'
import { Company } from 'depoto-core/src/entities'
import { NgSelectConfig } from '@ng-select/ng-select'
import * as Sentry from '@sentry/angular'
import { HttpClient } from '@angular/common/http'
import { QueryClient } from '@tanstack/angular-query-experimental'
import { interval, of, Subscription } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { User } from 'depoto-core/src/entities/User'
import packageJson from '../../package.json'
import { environment } from '../environments/environment'
import { getSupportedLanguage } from './configs/languages'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  environment = environment
  company: Company
  isShutdownForOverdue = false
  private subscriptions = new Subscription()
  isLoading = false
  isBaseModelLoaded = false
  isMainCompany = false
  private lastNavTrigger: 'imperative' | 'popstate' | 'hashchange' | null = null
  // Prevent multiple logout handlers causing repeated actions
  private hasHandledLogout = false
  // Persist scroll positions per URL for selected listing routes
  private readonly listingRoutes = new Set<string>([
    // Add listing route segments here
    'products',
    'payments',
    'parameters',
    'categories',
    'customers',
    'depots',
    'producers',
    'suppliers',
    'tags',
    'vats',
    'vouchers',
    'orders',
    'users',
    'checkouts',
    'consents',
    'company-carriers',
    'price-levels',
    'purchase-orders',
    'product-move-packs',
    'sold-items',
  ])

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private core: CoreService,
    private readonly httpClient: HttpClient,
    private ngSelectConfig: NgSelectConfig,
    private translateService: TranslateService,
    private readonly queryClient: QueryClient,
    private readonly logoutService: LogoutService
  ) {
    window['DEBUG'] = {
      exception: false,
      logger: false,
      history: false,
    }
    Events.listen('overdue:shutdown', () => {
      this.isShutdownForOverdue = true
    })
    // todo decide when is all loaded in, on every page
    Events.listen('init:loading', () => {
      // console.log('LALALA')
      this.isLoading = false
      // this.isBaseModelLoaded = true
    })
    this.core.services.user.currentUserEmitter.subscribe().then((user: User) => {
      this.isBaseModelLoaded = !!user?.id
      if (user) {
        this.translateService.use(getSupportedLanguage(user.locale))
      }
    })
    this.translateService.get('select-config.add-tag').subscribe(translation => {
      this.ngSelectConfig.addTagText = translation
    })
    this.translateService.get('select-config.clear-all').subscribe(translation => {
      this.ngSelectConfig.clearAllText = translation
    })
    this.translateService.get('select-config.loading').subscribe(translation => {
      this.ngSelectConfig.loadingText = translation
    })
    this.translateService.get('select-config.not-found').subscribe(translation => {
      this.ngSelectConfig.notFoundText = translation
    })
    this.translateService.get('select-config.search').subscribe(translation => {
      this.ngSelectConfig.typeToSearchText = translation
    })
  }

  async ngOnInit() {
    this.listenForRoutingUpdates()
    this.setupScrollRestoration()
    this.checkVersion()
    this.loadSideMenuState()
    this.core.services.oauth.onLogoutEmitter.subscribe().then(() => {
      if (this.hasHandledLogout) return
      this.hasHandledLogout = true
      // Ensure no stale query data survives logout
      ;(async () => {
        try {
          // 1) Cancel any in-flight queries so they can't write back stale data
          await this.queryClient.cancelQueries({ type: 'all' } as any)
        } catch {}
        try {
          // 2) Remove all queries from the cache
          this.queryClient.removeQueries()
        } catch {}
        try {
          // 3) Clear remaining caches (mutations, listeners)
          this.queryClient.clear()
        } catch {}
      })()
      // Abort any in-flight HTTP requests at the transport layer as well
      try {
        this.core.services.http.abortAllRequests()
      } catch {}
      // Clear app local storage caches unrelated to auth and per-user stale state
      try {
        const email: string =
          this.core.services.storage.getSync(this.core.services.storage.keys.auth.email) ||
          this.core.services.oauth.session.email
        // this.core.cleanLocalStorage(email, 'logout')
      } catch (e) {
        console.warn('Failed to clean localStorage on logout', e)
      }
      // Clear session scroll caches
      try {
        const keys: string[] = []
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i)
          if (key && key.startsWith('scroll:')) keys.push(key)
        }
        keys.forEach(k => sessionStorage.removeItem(k))
      } catch {}
      // Do not navigate here to avoid potential redirect loops with AuthGuard
      // UI should respond to unauthenticated state and show login as designed
    })
    this.core.services.user.currentUserEmitter.subscribe().then(async user => {
      // this.isBaseModelLoaded = false
      if (user?.company?.children?.length) {
        this.isMainCompany = true
      } else {
        this.isMainCompany = false
      }
      // A new authenticated session has started; allow future logout handling again
      if (user?.id) {
        this.hasHandledLogout = false
      }
      if (user?.id) {
        Events.dispatch('app:user:loaded')
        Sentry.setUser({ id: user.id, username: `${user.name} ${user.lastName}`, email: user.email })
        Sentry.setTag('company', user.company.name)
        // await this.core.loadLists()
      } else {
        Sentry.setUser(null)
        Sentry.setTag('company', 'undefined')
      }
    })
    // this.isBaseModelLoaded = true
    // todo nejak checkovat ze se naloadovaly tyhle queries co potrebuju i guess?
    // this.core.onCoreEntityListsLoaded.subscribe(lists => {
    //   this.isBaseModelLoaded = true
    // })
  }

  public isAuthenticated(): boolean {
    return this.core.services.oauth.isAuthenticated()
  }

  logout() {
    this.logoutService.logout()
  }

  listenForRoutingUpdates() {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => this.route),
        map(route => {
          if (route.firstChild) {
            route = route.firstChild
          }
          return route
        }),
        filter(route => route.outlet === 'primary')
      )
      .subscribe(route => {
        this.dispatchAnalyticsEvent()
      })
  }

  /**
   * Ensure that forward navigations scroll to top, while Back/Forward restore
   * the previous scroll position. This complements RouterModule.forRoot({ scrollPositionRestoration: 'enabled' })
   * and covers cases where the scroll container is body/html or a custom wrapper.
   */
  private setupScrollRestoration(): void {
    this.router.events.subscribe(evt => {
      if (evt instanceof NavigationStart) {
        // Remember what triggered the navigation so we can decide behavior on end
        this.lastNavTrigger = evt.navigationTrigger as any

        // Save current scroll for listings before leaving the page
        const currentUrl = this.router.url
        if (this.isListingUrl(currentUrl)) {
          this.saveScrollForUrl(currentUrl)
        }
      }

      if (evt instanceof Scroll) {
        // Angular emits Scroll on popstate/back/forward and for anchors
        if (evt.position) {
          // Restore previous position on Back/Forward. Do it asynchronously to ensure DOM/layout is ready.
          this.scheduleScrollRestore(evt.position)
        } else if (evt.anchor) {
          // Anchor scrolling inside our container if present
          const anchorEl = document.getElementById(evt.anchor)
          if (anchorEl) {
            anchorEl.scrollIntoView({ behavior: 'auto', block: 'start' })
          }
        }
      }

      if (evt instanceof NavigationEnd) {
        // For imperative navigations (clicks, router.navigate), force scroll to top.
        // Let Back/Forward be handled by the Scroll event above or Router's built-in restoration.
        if (this.lastNavTrigger === 'imperative' || this.lastNavTrigger === 'hashchange') {
          const targetUrl = this.router.url
          if (this.isListingUrl(targetUrl)) {
            const pos = this.getSavedScrollForUrl(targetUrl)
            if (pos) this.scheduleScrollRestore(pos)
            else this.scrollToTop()
          } else {
            this.scrollToTop()
          }
        }

        // On Back/Forward (popstate), some browsers restore window scroll but not custom containers.
        // Also, restoration may fire before the view is fully rendered. Apply our saved position too.
        if (this.lastNavTrigger === 'popstate') {
          const targetUrl = this.router.url
          if (this.isListingUrl(targetUrl)) {
            const saved = this.getSavedScrollForUrl(targetUrl)
            if (saved) this.scheduleScrollRestore(saved)
          }
        }
        // reset after handling
        this.lastNavTrigger = null
      }
    })
  }

  private scrollToTop(): void {
    try {
      // Window/document
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      // Custom wrapper (AdminLTE-like layout)
      const wrap = document.querySelector('.content-wrapper') as HTMLElement | null
      if (wrap) wrap.scrollTop = 0
    } catch {}
  }

  private scrollToPosition(position: [number, number]): void {
    const [x, y] = position || [0, 0]
    try {
      window.scrollTo({ left: x, top: y, behavior: 'auto' })
      document.documentElement.scrollLeft = x
      document.documentElement.scrollTop = y
      document.body.scrollLeft = x
      document.body.scrollTop = y
      const wrap = document.querySelector('.content-wrapper') as HTMLElement | null
      if (wrap) {
        wrap.scrollLeft = x
        wrap.scrollTop = y
      }
    } catch {}
  }

  /**
   * Apply scroll restoration after the DOM has rendered the new view.
   * Using double rAF helps ensure layout/height is updated before scrolling.
   */
  private scheduleScrollRestore(position: [number, number]): void {
    const apply = () => this.scrollToPosition(position)
    // Double rAF: next frame, then after layout of that frame
    requestAnimationFrame(() => requestAnimationFrame(apply))
    // As a fallback (in case rAF timing misses), also queue a microtask timeout
    setTimeout(apply, 50)
  }

  // ===== Scroll persistence helpers for listings =====
  private getPathLastSegment(url: string): string | null {
    try {
      const path = (url || '').split('?')[0]
      const parts = path.split('/').filter(Boolean)
      return parts.length ? parts[parts.length - 1] : null
    } catch {
      return null
    }
  }

  private isListingUrl(url: string): boolean {
    const last = this.getPathLastSegment(url)
    return !!last && this.listingRoutes.has(last)
  }

  private readCurrentScrollPosition(): [number, number] {
    try {
      const wrap = document.querySelector('.content-wrapper') as HTMLElement | null
      if (wrap && (wrap.scrollTop || wrap.scrollLeft)) {
        return [wrap.scrollLeft || 0, wrap.scrollTop || 0]
      }
      // Fallback to window/body
      const x = window.scrollX || document.documentElement.scrollLeft || document.body.scrollLeft || 0
      const y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
      return [x, y]
    } catch {
      return [0, 0]
    }
  }

  private makeStorageKey(url: string): string {
    return `scroll:${(url || '').split('#')[0]}`
  }

  private saveScrollForUrl(url: string): void {
    try {
      const key = this.makeStorageKey(url)
      const pos = this.readCurrentScrollPosition()
      sessionStorage.setItem(key, JSON.stringify(pos))
    } catch {}
  }

  private getSavedScrollForUrl(url: string): [number, number] | null {
    try {
      const key = this.makeStorageKey(url)
      const raw = sessionStorage.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed) && parsed.length === 2) return [Number(parsed[0]) || 0, Number(parsed[1]) || 0]
      return null
    } catch {
      return null
    }
  }

  loadSideMenuState() {
    const sidebarState: string = this.core.services.storage.getSync(this.core.services.storage.keys.app.sideMenuState)
    if (!sidebarState || sidebarState === 'collapsed') {
      const bodyElement = document.getElementsByTagName('body')[0]
      bodyElement.classList.add('sidebar-closed')
      if (!bodyElement.classList.contains('sidebar-collapse')) {
        bodyElement.classList.add('sidebar-collapse')
      }
    }
  }

  toggleLeftMenu() {
    const bodyElement = document.getElementsByTagName('body')[0]
    bodyElement.classList.add('sidebar-closed')
    bodyElement.classList.add('sidebar-collapse')
    bodyElement.classList.remove('sidebar-open')
  }

  dispatchAnalyticsEvent() {
    // must be initialized from index.html
    const _paq = window['_paq'] || []
    _paq.push(['setReferrerUrl', window.location.href])
    const currentUrl = `${window.location.hash.substr(1)}`
    _paq.push(['setCustomUrl', currentUrl])
    _paq.push(['setDocumentTitle', document.title])
    _paq.push(['deleteCustomVariables', 'page'])
    _paq.push(['setGenerationTimeMs', 0])
    _paq.push(['trackPageView'])
    _paq.push(['MediaAnalytics::scanForMedia', document])
    _paq.push(['FormAnalytics::scanForForms', document])
    _paq.push(['trackContentImpressionsWithinNode', document])
    Events.dispatch('app:navigation-end')
  }

  private checkVersion(): void {
    const url = this.getUrlForCheckingAppVersion()
    if (!url) {
      // don't check for latest version on localhost
      return
    }
    this.fetchAppVersion(url).subscribe(resp => {
      if (resp && packageJson.version !== resp.version) {
        this.onNewAppVersion()
      }
    })
    this.subscriptions.add(
      interval(2 * 60 * 1000)
        .pipe(
          mergeMap(() => {
            if (document.hasFocus()) {
              return this.fetchAppVersion(url)
            }

            return of(null)
          })
        )
        .subscribe((response: any) => {
          if (response && response.version !== packageJson.version) {
            this.onNewAppVersion()
          }
        })
    )
  }

  private fetchAppVersion(url: string) {
    return this.httpClient.get<{ version: string; date: string }>(url).pipe(catchError(() => of(null)))
  }

  private getUrlForCheckingAppVersion(): string {
    let result
    switch (location.host) {
      case 'app.depoto.cz.tomatomstage.cz':
        result = 'https://app.depoto.cz.tomatomstage.cz/version.json'
        break
      case 'app.depoto.cz':
        result = 'https://app.depoto.cz/version.json'
        break
    }
    return result
  }

  private onNewAppVersion(): void {
    if (confirm(this.translateService.instant('app.new-version'))) {
      if (window['nw_clear_cache']) {
        window['nw_clear_cache']()
        alert(this.translateService.instant('app.upgrade'))
      }
      window.location.reload()
    }
  }
}

// @Injectable({ providedIn: 'root' })
// export class TodoService {
//   private http = inject(HttpClient)
//
//   getTodos(): Promise<Todo[]> {
//     return lastValueFrom(
//         this.http.get<Todo[]>('https://jsonplaceholder.typicode.com/todos'),
//     )
//   }
//
//   addTodo(todo: Todo): Promise<Todo> {
//     return lastValueFrom(
//         this.http.post<Todo>('https://jsonplaceholder.typicode.com/todos', todo),
//     )
//   }
// }
//
// interface Todo {
//   id: string
//   title: string
// }
